import { useState, useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  Edit,
  Delete,
  Add,
  ListAltRounded,
  WarningAmber,
  ManageAccountsRounded,
  SettingsInputComponentRounded
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import ScopesUpdate from "./scopes-update";
import ScopeDetails from "./scope-details";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Scopes Management component powered by Material React Table
function Scopes() {
  const { createScope, updateScope, deleteScope, getScope, getAllScopes } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedScopeName, setSelectedScopeName] = useState(null);
  const [selectedScopeData, setSelectedScopeData] = useState({});

  // Safety Confirmation States
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, scope: null });
  const [editConfirm, setEditConfirm] = useState({ open: false, scope: null });

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: scopesDataLoading,
    data: scopesData,
    refetch,
    error: fetchError,
  } = useQuery(
    ["scopes", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name", "description", "resource_server_name"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/oauth/scopes`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!getAllScopes }
  );

  // Mutations
  const { mutate: deleteScopeMutate, isLoading: isDeleting } = useMutation((name) =>
    axiosPrivate.delete(`/oauth/scopes/${name}`),
    {
      onSuccess: () => {
        enqueueSnackbar("Scope deleted successfully", { variant: "success" });
        setDeleteConfirm({ open: false, scope: null });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (scope) => {
    setSelectedScopeName(scope.name);
    setView(true);
    setOpen(true);
  };

  const handleEditRequest = (scope) => {
    setSelectedScopeData(scope);
    setEditConfirm({ open: true, scope });
  };

  const handleProceedEdit = () => {
    setSelectedScopeName(selectedScopeData.name);
    setUpdate(true);
    setView(false);
    setOpen(true);
    setEditConfirm({ open: false, scope: null });
  };

  const handleOpenCreate = () => {
    setUpdate(false);
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setUpdate(false);
    setView(false);
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Scope Name",
      size: 200,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.lighter', color: 'primary.main', borderRadius: 2 }}>
            <SettingsInputComponentRounded fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {row.original.resource_server_name || 'Global Scope'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "description",
      header: "Role & Permission",
      size: 250,
      Cell: ({ cell }) => (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', maxWidth: '250px', whiteSpace: 'normal' }}>
          {cell.getValue()}
        </Typography>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: scopesData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: scopesData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: scopesDataLoading, pagination, sorting },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Details">
          <IconButton size="small" onClick={() => handleView(row.original)} sx={{ opacity: 0.7 }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        {updateScope && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditRequest(row.original)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {deleteScope && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, scope: row.original })} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex', flexShrink: 0 }}>
            <ListAltRounded sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              OAuth 2.0 Scopes
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {scopesData?.data?.meta_data?.total || 0} permissions defined
            </Typography>
          </Box>
        </Stack>
        {createScope && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Create Scope
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '12px',
        py: 2,
      },
    },
  });

  return fetchError ? (
    <Error error={fetchError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Modals for Detail / Update */}
      <Modal open={open} onClose={handleModalClose}>
        <Box sx={{ height: "100vh", overflowY: "auto" }}>
          {view ? (
            <ScopeDetails handleViewModalClose={handleModalClose} scopeName={selectedScopeName} />
          ) : (
            <ScopesUpdate handleModalClose={handleModalClose} isNew={!update} scopeData={selectedScopeData} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation */}
      <SafetyDialog
        open={deleteConfirm.open}
        title="Delete Scope?"
        content={`The scope "${deleteConfirm.scope?.name}" will be permanently removed. This may break applications that rely on this permission. Proceed?`}
        onConfirm={() => deleteScopeMutate(deleteConfirm.scope?.name)}
        onCancel={() => setDeleteConfirm({ open: false, scope: null })}
        isLoading={isDeleting}
        color="error"
      />

      {/* Edit Confirmation */}
      <SafetyDialog
        open={editConfirm.open}
        title="Modify Permission?"
        content={`Adjusting the definition of "${editConfirm.scope?.name}" impacts all client integrations using it. Proceed?`}
        onConfirm={handleProceedEdit}
        onCancel={() => setEditConfirm({ open: false, scope: null })}
        color="warning"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog
      open={open}
      onClose={() => !isLoading && onCancel()}
      PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: `${color}.lighter`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Scopes;
